import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmployeeStatus, Role } from '@cfl/shared';

import { api, type ApiError } from '@/lib/api';

/** Mirrors EmployeeSummary in apps/api/src/people/people.service.ts. */
export interface Employee {
  id: string;
  email: string;
  /** The company employee ID. Null until HR issues one. */
  employeeCode: string | null;
  name: string;
  role: Role;
  status: EmployeeStatus;
  departmentId: string | null;
  departmentName: string | null;
  designationId: string | null;
  designationTitle: string | null;
  reportingManagerId: string | null;
  reportingManagerName: string | null;
  directReportCount: number;
  joinedAt: string | null;
  lastLoginAt: string | null;
  hasSignedIn: boolean;
  needsOnboarding: boolean;
}

export interface DepartmentNode {
  id: string;
  name: string;
  code: string | null;
  parentId: string | null;
  headId: string | null;
  headName: string | null;
  employeeCount: number;
  children: DepartmentNode[];
}

export interface Designation {
  id: string;
  title: string;
  employeeCount: number;
}

export interface EmployeeFilters {
  search?: string;
  departmentId?: string;
  role?: Role;
  status?: EmployeeStatus;
  needsOnboarding?: boolean;
  page?: number;
  pageSize?: number;
}

interface Paged<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

function toQueryString(filters: EmployeeFilters): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== '' && value !== false) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const peopleKeys = {
  list: (filters: EmployeeFilters) => ['people', filters] as const,
  departments: ['departments'] as const,
  designations: ['designations'] as const,
};

export function useEmployees(filters: EmployeeFilters) {
  return useQuery<Paged<Employee>, ApiError>({
    queryKey: peopleKeys.list(filters),
    queryFn: () => api.get<Paged<Employee>>(`/people${toQueryString(filters)}`),
    // Keeps the previous page on screen while the next loads, so the table does
    // not collapse to a spinner on every keystroke of the search box.
    placeholderData: (previous) => previous,
  });
}

/**
 * A colleague you can name, from the directory every signed-in user may read.
 * Not `Employee`: that is the HR directory row and carries role, status and
 * employee code, which this endpoint deliberately does not return.
 */
export interface DirectoryEntry {
  id: string;
  name: string;
  email: string;
  departmentName: string | null;
  designationTitle: string | null;
  reportingManagerId: string | null;
}

/**
 * The colleague picker. Uses /people/directory rather than /people, which is
 * HR-only - pointing this at /people is what left a plain employee staring at an
 * empty "Who" list on the recognition dialog.
 */
export function useDirectory(search: string | undefined) {
  return useQuery<DirectoryEntry[], ApiError>({
    queryKey: ['people', 'directory', search ?? ''],
    queryFn: () =>
      api.get<DirectoryEntry[]>(
        `/people/directory${search ? `?search=${encodeURIComponent(search)}` : ''}`,
      ),
    staleTime: 30_000,
  });
}

/** Your own direct reports. Scoped to the caller server-side, so no role needed. */
export function useMyReports(enabled = true) {
  return useQuery<Employee[], ApiError>({
    queryKey: ['people', 'my-reports'],
    queryFn: () => api.get<Employee[]>('/people/my-reports'),
    enabled,
    staleTime: 60_000,
  });
}

export function useDepartments() {
  return useQuery<DepartmentNode[], ApiError>({
    queryKey: peopleKeys.departments,
    queryFn: () => api.get<DepartmentNode[]>('/departments'),
    staleTime: 5 * 60_000,
  });
}

export function useDesignations() {
  return useQuery<Designation[], ApiError>({
    queryKey: peopleKeys.designations,
    queryFn: () => api.get<Designation[]>('/designations'),
    staleTime: 5 * 60_000,
  });
}

/** Flattens the department tree into "Parent > Child" labels for select inputs. */
export function flattenDepartments(
  nodes: DepartmentNode[],
  prefix = '',
): Array<{ id: string; label: string; employeeCount: number }> {
  return nodes.flatMap((node) => {
    const label = prefix ? `${prefix} > ${node.name}` : node.name;
    return [
      { id: node.id, label, employeeCount: node.employeeCount },
      ...flattenDepartments(node.children, label),
    ];
  });
}

function useInvalidatePeople() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['people'] });
    void queryClient.invalidateQueries({ queryKey: peopleKeys.departments });
    // The goal overview prints each person's name, department and reporting manager,
    // and onboarding or a promotion can also have created a whole goal set behind it.
    void queryClient.invalidateQueries({ queryKey: ['goal-sets'] });
  };
}

export interface CreateEmployeeInput {
  email: string;
  /** Empty string clears it; the API uppercases whatever is sent. */
  employeeCode?: string | null;
  name: string;
  role: Role;
  departmentId?: string | null;
  designationId?: string | null;
  reportingManagerId?: string | null;
  joinedAt?: string | null;
}

/**
 * A saved employee, plus what their job title's pinned KRA group did.
 *
 * `autoAssigned` is null when there was nothing to do — no job title, or a title no
 * group claims — which is the ordinary case. A row with `assigned: false` is the one
 * worth showing: their goals did NOT appear, and it says why.
 */
export interface EmployeeSaveResult extends Employee {
  autoAssigned: {
    employeeName: string;
    assigned: boolean;
    reason: string | null;
    message: string | null;
  } | null;
}

export function useCreateEmployee() {
  const invalidate = useInvalidatePeople();
  return useMutation<EmployeeSaveResult, ApiError, CreateEmployeeInput>({
    mutationFn: (input) => api.post<EmployeeSaveResult>('/people', input),
    onSuccess: invalidate,
  });
}

export function useUpdateEmployee() {
  const invalidate = useInvalidatePeople();
  return useMutation<EmployeeSaveResult, ApiError, { id: string } & Partial<CreateEmployeeInput>>({
    mutationFn: ({ id, ...body }) => api.patch<EmployeeSaveResult>(`/people/${id}`, body),
    onSuccess: invalidate,
  });
}

/** Separate endpoint because the server also revokes that person's sessions. */
export function useChangeRole() {
  const invalidate = useInvalidatePeople();
  return useMutation<Employee, ApiError, { id: string; role: Role; reason?: string }>({
    mutationFn: ({ id, ...body }) => api.patch<Employee>(`/people/${id}/role`, body),
    onSuccess: invalidate,
  });
}

/**
 * Set someone's sign-in password (password sign-in only). Write-only - nothing ever reads
 * one back - and the server ends that person's sessions.
 */
export function useSetPassword() {
  return useMutation<void, ApiError, { id: string; password: string }>({
    mutationFn: ({ id, password }) => api.put<void>(`/people/${id}/password`, { password }),
  });
}

export function useChangeStatus() {
  const invalidate = useInvalidatePeople();
  return useMutation<
    Employee,
    ApiError,
    { id: string; status: EmployeeStatus; reason?: string; exitedOn?: string }
  >({
    mutationFn: ({ id, ...body }) => api.patch<Employee>(`/people/${id}/status`, body),
    onSuccess: invalidate,
  });
}

/**
 * Email → id, for the addresses a bulk sheet names as reporting managers.
 *
 * One targeted search per distinct address rather than pulling the company into the
 * browser: `/people` is paginated and `/people/directory` caps at 50, so a
 * load-the-whole-list lookup would quietly stop finding managers the day the org
 * outgrows one page — and would report a real manager as "not found", which is worse
 * than not checking at all. A bulk-hire sheet names a handful of managers, so a
 * handful of requests is the whole cost.
 *
 * `search` matches name OR email, so the result is narrowed back to an exact address:
 * searching raj@innoctive.com must never resolve to raja@innoctive.com.
 */
export async function findEmployeeIdsByEmail(emails: string[]): Promise<Map<string, string>> {
  const found = new Map<string, string>();
  await Promise.all(
    [...new Set(emails.map((e) => e.trim().toLowerCase()))]
      .filter((email) => email !== '')
      .map(async (email) => {
        const page = await api.get<Paged<Employee>>(
          `/people?search=${encodeURIComponent(email)}&pageSize=5`,
        );
        const match = page.items.find((p) => p.email.toLowerCase() === email);
        if (match) found.set(email, match.id);
      }),
  );
  return found;
}
